import React, { FC, useRef, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Approval } from 'api/approval';
import { ProjectId } from 'api/models';
import {
  ResourceList,
  ResourceListSectionConfig,
} from 'components/molecules/resource-list';
import { RootState } from 'data';
import { ResourceSectionType } from 'data/documents';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { selectTasksByProjectId } from 'data/tasks/selectors';
import { FieldsStore, StatusField } from 'stores/fields';

interface ResourceListSectionProps {
  config: ResourceListSectionConfig;
}

export const ResourceListSectionBase: FC<ResourceListSectionProps> = ({
  config,
}) => {
  const { resourceIds, resourceSectionType } = config;

  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const projectId = activeStatusUpdate?.project.projectId;
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const status = FieldsStore.getTaskStatusField(fieldsStore)[1] as StatusField;

  const projectTasks = useSelector(
    selectTasksByProjectId(ProjectId(projectId as number)),
    shallowEqual,
  );

  const getSectionResources = useCallback(() => {
    const tasks = projectTasks.filter((task) => {
      return resourceIds.indexOf(task.taskId) > -1;
    });
    const approvalTasks = projectTasks.filter((task) => task.approvals?.length);
    const approvalEntries = approvalTasks.reduce((acc, task) => {
      const decoratedApprovals = task.approvals?.map((apprval) => {
        return {
          ...apprval,
          task,
        };
      }, []);

      return [...acc, ...(decoratedApprovals || [])];
    }, [] as Approval[]);

    const approvals = approvalEntries.filter((approval) => {
      return resourceIds.indexOf(approval.approvalId) > -1;
    });

    return resourceSectionType === ResourceSectionType.Approval
      ? approvals
      : tasks;
  }, [projectTasks, resourceIds, resourceSectionType]);

  const sectionResources = getSectionResources();

  const tableRef = useRef<HTMLDivElement>(null);

  return projectId && sectionResources ? (
    <ResourceList
      projectId={projectId}
      refTo={tableRef}
      data={sectionResources}
      config={config}
      statusField={status}
    />
  ) : null;
};
